import { useState } from 'react';
import { Send, ChevronLeft, Eraser, StickyNote, SlidersHorizontal, HelpCircle, LayoutDashboard } from 'lucide-react';
import { BrainLogo } from './BrainLogo';
import { MapSwitcher } from './MapSwitcher';
import { PersonalizationPanel } from './PersonalizationPanel';
import { AuthButton } from './AuthButton';
import { ThemeToggle } from './ThemeToggle';
import logoIcon from '../assets/mobile-app-icon--person-with-dark-blue-hair-in-thi.png';
import { useT } from '../i18n';

/**
 * Sidebar component - Chat interface and controls
 * @param {Object} props - Component props
 * @param {Array} props.messages - Chat messages
 * @param {boolean} props.isLoading - Whether AI is loading
 * @param {string} props.input - Current input value
 * @param {Function} props.onInputChange - Function to handle input change
 * @param {Function} props.onSendMessage - Function to send message
 * @param {Object} props.chatEndRef - Ref to chat end element
 */
export const Sidebar = ({
  messages,
  isLoading,
  input,
  onInputChange,
  onSendMessage,
  onClearChat,
  onChangeProvider,
  providerName = 'AI',
  chatEndRef,
  maps,
  currentMapId,
  currentMapName,
  onSwitchMap,
  onCreateMap,
  onDeleteMap,
  onRenameMap,
  onExportMap,
  onImportMap,
  onImportFromBook,
  onStartQuiz,
  isInfoGatheringActive,
  onStopInfoGathering,
  onToggleSidebar,
  suggestions = [],
  onSuggestionClick,
  onCopyToPostIt,
  user,
  onSignIn,
  onSignOut,
  currentMode = 'brainstormer',
  onModeChange,
  userContext = '',
  onUserContextChange,
  mapContext = '',
  onMapContextChange,
  onImproveUserContext,
  onImproveMapContext,
  loadingStatus = '',
  onShowTutorial,
}) => {
  const t = useT();
  const [clickedChip, setClickedChip] = useState(null);
  const [showPersonalization, setShowPersonalization] = useState(false);

  const handleChipClick = (text) => {
    setClickedChip(text);
    onSuggestionClick?.(text);
    setTimeout(() => setClickedChip(null), 600);
  };

  const iconBtn = "w-9 h-9 flex items-center justify-center border-2 rounded-[14px] transition-all hover:scale-105 active:scale-95";

  return (
    <div
      className="w-full h-full min-h-0 flex flex-col"
      style={{
        background: 'var(--bg-dim)',
        borderRight: '1px solid var(--border-s)',
      }}
    >
      {/* Header */}
      <div
        className="px-5 pt-5 pb-3 flex flex-col gap-3 shrink-0 sticky top-0 z-10 backdrop-blur-sm"
        style={{
          borderBottom: '1px solid var(--border-s)',
          background: 'color-mix(in srgb, var(--bg-dim) 95%, transparent)',
        }}
      >
        {/* Riga 1: logo (sx) + azioni (dx) */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleSidebar}
              className={`w-10 h-10 flex items-center justify-center border-2 rounded-[14px] transition-all hover:scale-105 active:scale-95 shadow-sm`}
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
              }}
              title={t('sidebar.backToMap')}
            >
              <ChevronLeft size={24} />
            </button>
            <BrainLogo size="lg" />
          </div>
          <div className="flex items-center gap-1.5">
            <AuthButton user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
            <ThemeToggle />
            <a
              href="/whiteboard"
              className={iconBtn}
              style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              title="Lavagna AI"
            >
              <LayoutDashboard size={16} />
            </a>
            <button
              onClick={onClearChat}
              className={iconBtn}
              style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              title={t('sidebar.clearChat')}
            >
              <Eraser size={16} />
            </button>
            <button
              onClick={onShowTutorial}
              className={iconBtn}
              style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              title="Tutorial"
            >
              <HelpCircle size={16} />
            </button>
            <button
              onClick={() => setShowPersonalization(v => !v)}
              className={iconBtn}
              style={{
                background: showPersonalization ? 'color-mix(in srgb, var(--accent) 20%, transparent)' : 'var(--surface)',
                borderColor: showPersonalization ? 'var(--accent)' : 'var(--border)',
                color: showPersonalization ? 'var(--accent)' : 'var(--text-muted)',
              }}
              title={t('sidebar.customize')}
            >
              <SlidersHorizontal size={16} />
            </button>
          </div>
        </div>
        {/* Riga 2: map switcher full width */}
        <MapSwitcher
          maps={maps}
          currentMapId={currentMapId}
          currentMapName={currentMapName}
          onSwitchMap={onSwitchMap}
          onCreateMap={onCreateMap}
          onDeleteMap={onDeleteMap}
          onRenameMap={onRenameMap}
          onExportMap={onExportMap}
          onImportMap={onImportMap}
          onImportFromBook={onImportFromBook}
          onStartQuiz={onStartQuiz}
          onChangeProvider={onChangeProvider}
          providerName={providerName}
        />
      </div>

      {/* Pannello personalizzazione — slide down */}
      {showPersonalization && (
        <PersonalizationPanel
          currentMode={currentMode}
          onModeChange={onModeChange}
          userContext={userContext}
          onUserContextChange={onUserContextChange}
          mapContext={mapContext}
          onMapContextChange={onMapContextChange}
          onImproveUserContext={onImproveUserContext}
          onImproveMapContext={onImproveMapContext}
        />
      )}

      {/* Chat */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5 space-y-3">
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center px-6 animate-[fadeIn_0.5s_ease-out]">
            <img
              src={logoIcon}
              alt="The Thinker"
              className="w-20 h-20 rounded-[24px] mb-5 animate-[float_3s_ease-in-out_infinite]"
              style={{ boxShadow: '0 8px 24px color-mix(in srgb, var(--accent) 20%, transparent)' }}
            />
            <h2 className="text-xl font-bold mb-3 tracking-tight" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
              {t(`sidebar.emptyState.${currentMode || 'brainstormer'}.title`)}
            </h2>
            <p className="text-[15px] leading-relaxed max-w-[280px]" style={{ color: 'var(--text-muted)' }}>
              {t(`sidebar.emptyState.${currentMode || 'brainstormer'}.body`)}
            </p>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-[slideIn_0.3s_ease-out]`}
          >
            {msg.role === 'user' ? (
              <div
                className="group relative max-w-[82%] px-4 py-3 rounded-[14px] rounded-br-[4px] transition-all duration-200 hover:scale-[1.01]"
                style={{
                  background: 'var(--ctp-surface1)',
                  borderLeft: '3px solid var(--accent)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}
              >
                {msg.content}
              </div>
            ) : (
              <div
                className="group relative max-w-[90%] px-4 py-3 rounded-[14px] rounded-bl-[4px] transition-all duration-200"
                style={{
                  background: 'color-mix(in srgb, var(--ctp-surface0) 60%, transparent)',
                  borderLeft: '3px solid var(--accent-2)',
                  border: '1px solid color-mix(in srgb, var(--accent-2) 20%, transparent)',
                  borderLeftWidth: '3px',
                  borderLeftColor: 'var(--accent-2)',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {onCopyToPostIt && (
                  <button
                    onClick={() => onCopyToPostIt(msg.content)}
                    title={t('sidebar.copyToPostIt')}
                    className="absolute -bottom-2.5 -right-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 w-7 h-7 flex items-center justify-center rounded-full"
                    style={{
                      background: 'var(--surface)',
                      border: '2px solid var(--border)',
                      color: 'var(--text-dim)',
                    }}
                  >
                    <StickyNote size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start animate-[slideIn_0.3s_ease-out]">
            <div
              className="max-w-[82%] px-4 py-3 rounded-[14px] rounded-bl-[4px] flex flex-col gap-2"
              style={{
                background: 'color-mix(in srgb, var(--ctp-surface0) 60%, transparent)',
                borderLeft: '3px solid var(--accent-2)',
                border: '1px solid color-mix(in srgb, var(--accent-2) 20%, transparent)',
                borderLeftWidth: '3px',
                borderLeftColor: 'var(--accent-2)',
              }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--accent)', boxShadow: '0 0 6px var(--accent)' }}></div>
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--accent-2)', boxShadow: '0 0 6px var(--accent-2)', animationDelay: '200ms' }}></div>
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--accent)', boxShadow: '0 0 6px var(--accent)', animationDelay: '400ms' }}></div>
              </div>
              {loadingStatus && (
                <div className="text-[11px] font-semibold uppercase tracking-widest animate-pulse" style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  {loadingStatus}
                </div>
              )}
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div
        className="p-4 shrink-0"
        style={{ borderTop: '1px solid var(--border-s)' }}
      >
        {/* Suggestion chips */}
        {!isInfoGatheringActive && suggestions.length > 0 && (
          <div className="mb-3 flex flex-col gap-1.5">
            {suggestions.map((text, i) => (
              <button
                key={i}
                onClick={() => handleChipClick(text)}
                title={t('sidebar.expandMap')}
                className="text-left text-[12px] px-3 py-2 rounded-[10px] transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center gap-2"
                style={{
                  background: clickedChip === text ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'var(--surface)',
                  border: `1px solid ${clickedChip === text ? 'var(--accent)' : 'var(--border)'}`,
                  color: 'var(--text-muted)',
                }}
              >
                <span style={{ color: 'var(--accent-2)', fontSize: '10px' }}>↗</span>
                <span className="truncate">{text}</span>
              </button>
            ))}
          </div>
        )}

        {/* Stop info gathering button */}
        {isInfoGatheringActive && (
          <div className="mb-3">
            <button
              onClick={onStopInfoGathering}
              className="w-full px-4 py-2.5 rounded-xl transition-all font-medium flex items-center justify-center gap-2 animate-pulse"
              style={{ background: 'var(--warning)', color: 'var(--bg-deeper)' }}
            >
              <span className="text-lg">✋</span>
              {t('sidebar.iHaveEnoughContext')}
            </button>
          </div>
        )}

        <div className="relative group">
          <textarea
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSendMessage();
              }
            }}
            placeholder={t('sidebar.inputPlaceholder')}
            className="w-full px-4 py-3 pr-14 rounded-[16px] resize-none focus:outline-none transition-all duration-200"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              caretColor: 'var(--accent)',
            }}
            rows={3}
          />
          <button
            onClick={onSendMessage}
            disabled={!input.trim() || isLoading}
            className="absolute right-2.5 bottom-2.5 w-9 h-9 rounded-[12px] transition-all duration-200 flex items-center justify-center hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:scale-100"
            style={{
              background: (!input.trim() || isLoading) ? 'var(--surface-hi)' : 'var(--accent)',
              color: (!input.trim() || isLoading) ? 'var(--text-dim)' : 'var(--bg-deeper)',
            }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
